<script setup>
import { computed, onBeforeMount, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import { collectionV2, resourceV2 } from '@/composables/resource';
import { useStore } from '@/store';

import ListingCard from '@/components/Secondary/Marketplace/ListingCard.vue';
import SideNav from '@/views/marketplace/_helpers/layout/SideNav.vue';

const route = useRoute();
const router = useRouter();
const store = useStore();
const seller = resourceV2({
    url: `/api/marketplace/sellers/${route.params.id}`
});

const listings = collectionV2({
    url: `/api/marketplace/listings`,
    params: {
        filters: {
            status: 'active',
            seller_id: route.params.id,
        },
    },
});

const title = ref('Seller Profile');

const menuItems = computed(() => {
    return [
        {
            label: 'Edit',
            icon: 'icon-pencil',
            show: seller.data?.is_me,
            to: { name: 'marketplace.me.edit' },
        },
        {
            label: 'My Listings',
            icon: 'icon-tag-multiple',
            show: seller.data?.is_me,
            to: { name: 'marketplace.me.listings' },
        }
    ];
});

onBeforeMount(() => {
    seller.fetch({ swCache: true }).then(() => listings.fetch()).catch(() => {
        if (seller.notFound && store.state.user && route.params.id === store.state.user.id) {
            router.replace({ name: 'marketplace.sellers.create' });
        }
    });
});
</script>

<template>
    <layout-main>
        <head-title :value="title" />
        <SideNav />
        <layout-content>
            <content-header>
                <top-nav>
                    <top-nav-back hide-from-desktop />
                    <top-nav-title>
                        Marketplace Seller
                    </top-nav-title>

                    <top-nav-menu :items="menuItems" />
                </top-nav>
            </content-header>
            <content-body>
                <template v-if="seller.loadState === 'ready'">
                    <div class="p-4">
                        <div class="flex items-center gap-4">
                            <div class="self-start shrink-0">
                                <app-avatar :src="seller.data.profile.image.url" size="3xl" />
                            </div>
                            <div>
                                <h1 class="text-2xl font-bold select-text">{{ seller.data.profile.name }}</h1>
                                <app-status-badge
                                    :text="seller.data.profile.verified ? 'Verified' : 'Not Verified'"
                                    :icon="seller.data.profile.verified ? 'verified-user' : null"
                                    :color="seller.data.profile.verified ? 'primary' : 'danger'" />
                                <div class="text-sm text-muted mt-2">
                                    {{ seller.data.active_listings_count }} listings
                                </div>
                            </div>
                        </div>

                        <div class="mt-4">

                            <div class="flex flex-col gap-1 mt-2 text-white-foreground/80">
                                <div class="flex flex-row gap-1 items-center">
                                    <icon-user class="size-5" />
                                    <span class="text-sm">
                                        Joined Corperland in {{ $dayjs(seller.data.created_at).format('YYYY')}}
                                    </span>
                                </div>
                                <div v-if="seller.data.profile.phone" class="flex flex-row gap-1 items-center">
                                    <icon-call class="size-5" />
                                    <a :href="`tel:${seller.data.profile.phone}`" class="text-sm">
                                        {{ seller.data.profile.phone }}
                                    </a>
                                </div>
                                <div v-if="seller.data.profile.email" class="flex flex-row gap-1 items-center">
                                    <icon-alternate-email class="size-5" />
                                    <a :href="`mailto:${seller.data.profile.email}`" class="text-sm">
                                        {{ seller.data.profile.email}}
                                    </a>
                                </div>
                                <div v-if="seller.data.profile.website" class="flex flex-row gap-1 items-center">
                                    <icon-web class="size-5" />
                                    <a target="_blank" rel="noopener noreferrer" class="text-sm" :href="seller.data.profile.website">
                                        {{ seller.data.profile.website}}
                                    </a>
                                </div>
                                <div v-if="seller.data.profile.state" class="flex flex-row gap-1 items-center">
                                    <icon-address-marker class="size-5" />
                                    <span class="text-sm">
                                        {{ seller.data.profile.state.name}}
                                    </span>
                                </div>
                            </div>

                            <div class="text-sm mt-2">
                                <app-line-clamp :lines="2" class="text-sm">
                                    {{ seller.data.profile.description }}
                                </app-line-clamp>
                            </div>
                        </div>

                        <div v-if="seller.data.is_me && !seller.data.verified && (!seller.data.verification || seller.data.verification)" class="mt-4">
                            <app-button color="primary" capitalize full>Get Verified</app-button>
                        </div>
                    </div>

                    <div class="mt-4 p-4 border-t border-white-foreground/10">
                        <h2 class="text-base font-medium mb-4">Active Listings</h2>

                        <template v-if="!listings.fetched && listings.fetching">
                            <app-layout-skeleton :count="2" />
                        </template>

                        <div class="mt-12" v-else-if="listings.fetched && listings.data.length === 0">
                            <div class="text-2xl font-bold text-center mb-2">
                                No listings yet
                            </div>
                            <div class="text-base text-center text-muted leading-relaxed max-w-md mx-auto mb-6">
                                <template v-if="seller.data.is_me">
                                    Get your products in front of buyers and kickstart your journey on the Corperland Marketplace.
                                </template>
                                <template v-else>
                                    This seller hasn't listed any products yet. Check back later to see what they have in store.
                                </template>
                            </div>
                            <div v-if="seller.data.is_me" class="flex items-center justify-center">
                                <app-button as="router-link" :to="{ name: 'marketplace.listing.create' }" color="primary-border" normalcase>Create a listing</app-button>
                            </div>
                        </div>

                        <div v-else-if="listings.data.length > 0" class="grid grid-cols-2 md:grid-cols-3 gap-4">
                            <ListingCard v-for="listing in listings.data" :key="listing.id" :data="listing" />
                        </div>
                    </div>
                </template>

                <app-layout-skeleton v-else-if="seller.loadState === 'loading'" />
                <app-error-not-found v-else-if="seller.loadState === 'missing' && (!store.state.user || route.params.id !== store.state.user.id)" />
                <app-error-default v-else-if="seller.loadState === 'error'" />
            </content-body>
        </layout-content>
    </layout-main>
</template>
